/**
 * IDB CRUD для `pendingMedia` store — blob queue для R2 upload.
 *
 * Used by Phase D (PDF generation) + Phase 9+ (real R2 backend):
 *   - savePendingMedia() — после html2pdf.js generation, blob → IDB
 *   - getPendingMedia() — BackgroundSync worker берёт blob для upload
 *   - deletePendingMedia() — после successful R2 upload
 *   - incrementRetryCount() — на failed upload attempts
 *
 * Quota check (ADR-0019 §Q11.9):
 *   - Перед savePendingMedia() — проверка navigator.storage.estimate()
 *   - 1.5x overhead factor для безопасности
 *   - При quota exceeded → throw QuotaExceededError
 *
 * Cleanup policy (ADR-0019 §Q11.9):
 *   - После successful R2 upload + 7 дней retention → auto-delete blob
 *   - cleanupOldPendingMedia(retentionMs=7d) cron via requestIdleCallback
 *
 * References: ADR-0019 §Q11.2 (schema), §Q11.4 (BackgroundSync), §Q11.9 (quota)
 */

import { openDb, type PendingMediaRecord } from './database';

export const PENDING_MEDIA_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export class QuotaExceededError extends Error {
  constructor(
    public freeBytes: number,
    public neededBytes: number,
  ) {
    super(`QUOTA_EXCEEDED: need ${neededBytes} bytes, only ${freeBytes} free`);
    this.name = 'QuotaExceededError';
  }
}

/**
 * Check available storage quota before save.
 * Returns ok=true if free > size * 1.5 (overhead для IDB index + metadata).
 *
 * Falls back to ok=true в browsers без navigator.storage API
 * (Safari iOS — uses 50MB hard limit без preview).
 */
export async function checkStorageQuota(
  blobSize: number,
): Promise<{ ok: boolean; freeBytes: number }> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { ok: true, freeBytes: Number.POSITIVE_INFINITY };
  }
  const estimate = await navigator.storage.estimate();
  const quota = estimate.quota ?? 0;
  const usage = estimate.usage ?? 0;
  const freeBytes = quota - usage;
  const neededBytes = blobSize * 1.5;
  return { ok: freeBytes > neededBytes, freeBytes };
}

export async function savePendingMedia(record: PendingMediaRecord): Promise<void> {
  const check = await checkStorageQuota(record.blob.size);
  if (!check.ok) {
    throw new QuotaExceededError(check.freeBytes, record.blob.size * 1.5);
  }
  const db = await openDb();
  await db.put('pendingMedia', record);
}

export async function getPendingMedia(id: string): Promise<PendingMediaRecord | undefined> {
  const db = await openDb();
  return db.get('pendingMedia', id);
}

export async function deletePendingMedia(id: string): Promise<void> {
  const db = await openDb();
  await db.delete('pendingMedia', id);
}

export async function listPendingMediaByEntity(
  relatedEntityId: string,
): Promise<PendingMediaRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('pendingMedia', 'by-relatedEntityId', relatedEntityId);
}

export async function listAllPendingMedia(): Promise<PendingMediaRecord[]> {
  const db = await openDb();
  return db.getAll('pendingMedia');
}

export async function incrementRetryCount(id: string, now: number = Date.now()): Promise<number> {
  const db = await openDb();
  const tx = db.transaction('pendingMedia', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    throw new Error(`PENDING_MEDIA_NOT_FOUND: id=${id}`);
  }
  const updated: PendingMediaRecord = {
    ...existing,
    retryCount: existing.retryCount + 1,
    lastAttemptAt: now,
  };
  await tx.store.put(updated);
  await tx.done;
  return updated.retryCount;
}

/**
 * Cleanup pendingMedia older than retention (default 7 days).
 * Atomic single readwrite transaction — no race risk с parallel mutations
 * (Phase A4 fix for OQ-A3-3 advisory).
 *
 * Called from periodic cleanup (Phase A5 OnlineIndicator или requestIdleCallback).
 *
 * Returns count of deleted records.
 */
export async function cleanupOldPendingMedia(
  now: number = Date.now(),
  retentionMs: number = PENDING_MEDIA_RETENTION_MS,
): Promise<number> {
  const db = await openDb();
  const tx = db.transaction('pendingMedia', 'readwrite');
  const store = tx.store;
  const all = await store.getAll();
  let deleted = 0;
  for (const record of all) {
    if (now - record.createdAt > retentionMs) {
      await store.delete(record.id);
      deleted++;
    }
  }
  await tx.done;
  return deleted;
}
